import React, { useState, useEffect } from "react";
import { Tabs, Card, Row, Col, Spin, Table } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { fetchEnrollmentPolicyType, getEndorsementAnalysisReport } from "../../Action/Actions";
// import { useSelectedYear } from "../../Context/SelectYearContext"; // --- REMOVED
import { selectFinancialYears, selectSelectedYearId } from "../../Redux/financialYearSlice"; // +++ ADDED
import { getUserData } from "../login/LoginSlice";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { TeamOutlined, UserOutlined, FileAddOutlined, FireOutlined } from "@ant-design/icons";

const RESTRICTED_POLICY_TYPE_IDS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];

const EndorsementAnalysis = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { module_option_ids } = location.state || {};
  const AuthData = useSelector(getUserData);

  // +++ ADDED: Get financial year data from Redux store
  const selectedYearId = useSelector(selectSelectedYearId);
  const financialYears = useSelector(selectFinancialYears);

  // Find the selected year's display name from the year list
  const selectedYear = financialYears.find(year => year.id === selectedYearId)?.financial_year || "Not Selected";

  const [activeTab, setActiveTab] = useState("");
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  // const { selectedYear } = useSelectedYear(); // --- REMOVED
  const [policyTypeData, setPolicyTypeData] = useState({});
  const [loading, setLoading] = useState(false);
  const [endorsementData, setEndorsementData] = useState([]);
  const policyTypes = Object.keys(policyTypeData || {});
  const user = useSelector(getUserData);

  useEffect(() => {
    const fetchPolicyData = async () => {
      if (!AuthData?.corporate_id || !selectedYearId) {
        setLoading(false);
        setPolicyTypeData({});
        setSelectedPolicy(null);
        return;
      }
      try {
        setLoading(true);
        const response = await dispatch(fetchEnrollmentPolicyType(AuthData?.corporate_id));
        const policyTypeList = response?.policy_type_list || {};
        
        // NEW: Filter out restricted policy types
        const filtered_policyTypeList = {};
        for (const type in policyTypeList) {
          const typeName = String(type).toLowerCase();
          if (typeName.includes('marine') || typeName.includes('fire')) {
            continue;
          }

          const policies = policyTypeList[type] || [];
          const validPolicies = policies.filter(p => {
            const details = Array.isArray(p.policy_details) ? p.policy_details[0] : p.policy_details;
            const typeId = details?.policy_type_id || p.policy_type_id || p.policy_details?.policy_type_id;
            const isRestrictedId = RESTRICTED_POLICY_TYPE_IDS.includes(Number(typeId));
            return !isRestrictedId;
          });
          if (validPolicies.length > 0) {
            filtered_policyTypeList[type] = validPolicies;
          }
        }

        setPolicyTypeData(filtered_policyTypeList);
        
        // Get policy types that actually have data (not empty arrays)
        const availableTabs = Object.keys(filtered_policyTypeList || {}).filter(
          (tab) => filtered_policyTypeList[tab] && filtered_policyTypeList[tab].length > 0
        );
        
        // Use the first available type that has data
        const tabToUse = availableTabs.length > 0 ? availableTabs[0] : "";
        
        // Update activeTab to the first available type with data
        if (tabToUse && tabToUse !== activeTab) {
          setActiveTab(tabToUse);
        }
        
        // When data reloads (e.g., on year change), reset to the first policy of the current active tab
        const firstPolicy = policyTypeList[tabToUse]?.[0];
        setSelectedPolicy(firstPolicy || null);
      } catch (err) {
        console.error("Error fetching policy type:", err);
        setPolicyTypeData({});
        setSelectedPolicy(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPolicyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, AuthData?.corporate_id, selectedYearId]);

  useEffect(() => {
    // Clear previous data if no policy is selected
    if (!selectedPolicy) {
        setEndorsementData([]);
        return;
    }
    
    if (selectedPolicy && selectedYearId) {
      setLoading(true);
      // This action now implicitly uses the financial year ID from the Redux state
      dispatch(getEndorsementAnalysisReport(selectedPolicy.policy_id))
        .then((data) => {
          const formattedData = Object.entries(data).map(([month, values]) => ({
            key: month,
            month,
            employeeAddition: values["employee_addition"],
            dependentAddition: values["dependant_addition"],
            employeeCorrection: values["employee_correction"],
            dependentCorrection: values["dependant_correction"],
            employeeDeletion: values["employee_deletion"],
            dependentDeletion: values["dependant_deletion"],
          }));
          setEndorsementData(formattedData);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          console.error("Error fetching endorsement analysis report:", err);
        });
    }
    // +++ UPDATED dependency array to react to year changes
  }, [dispatch, selectedPolicy, selectedYearId]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    // Select the first policy of the newly activated tab from existing data
    const policy = policyTypeData[key]?.[0];
    setSelectedPolicy(policy || null);
  };

  const handlePolicyNumberClick = (key) => {
    // Find the policy object based on the key (either policy_number or policy-${index})
    const policy = policyTypeData[activeTab]?.find((p, index) => {
      const policyKey = p.policy_number || `policy-${index}`;
      return policyKey === key;
    });
    
    if (policy) {
      setSelectedPolicy(policy);
    }
  };
  
  const getTabIcon = (type) => {
    switch (type) {
      case "GMC":
        return <TeamOutlined style={{ marginRight: 8 }} />;
      case "GTL":
        return <UserOutlined style={{ marginRight: 8 }} />;
      case "GPA":
        return <TeamOutlined style={{ marginRight: 8 }} />;
      case "Parent policy":
        return <TeamOutlined style={{ marginRight: 8 }} />;
      case "Topup":
        return <FileAddOutlined style={{ marginRight: 8 }} />;
      case "Fire":
        return <FireOutlined style={{ marginRight: 8 }} />;
      default:
        return null;
    }
  };

  const chartData = endorsementData
    .filter((item) => item.key !== "total")
    .map((item) => ({
      month: item.month,
      employeeAddition: item.employeeAddition,
      dependentAddition: item.dependentAddition,
      employeeCorrection: item.employeeCorrection,
      dependentCorrection: item.dependentCorrection,
      employeeDeletion: item.employeeDeletion,
      dependentDeletion: item.dependentDeletion,
    }));

  const tabItems = policyTypes?.map((type) => ({
    key: type,
    label: (
      <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        {type}
        {getTabIcon(type)}
      </span>
    ),
    children: (
      <>
        <Tabs
          activeKey={selectedPolicy ? (selectedPolicy.policy_number || `policy-${policyTypeData[activeTab]?.findIndex(p => p.policy_id === selectedPolicy.policy_id)}`) : undefined}
          onChange={handlePolicyNumberClick}
          items={policyTypeData[activeTab]?.map((policy, index) => ({
            key: policy.policy_number || `policy-${index}`,
            label: policy.policy_number ? (
              policy.policy_number
            ) : (
              <span
                style={{
                  backgroundColor: "#FFFF00",
                  color: "#000000E0",
                  padding: "2px 8px",
                  fontSize: "12px",
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  borderRadius: "4px",
                  display: "inline-block",
                }}
              >
                Policy Number not issued
              </span>
            ),
          }))}
        />
        {selectedPolicy ? (
          <>
            <Card>
              <Card>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="employeeAddition"
                      fill="#8884d8"
                      name="Employee Addition"
                    />
                    <Bar
                      dataKey="dependentAddition"
                      fill="#82ca9d"
                      name="Dependent Addition"
                    />
                    <Bar
                      dataKey="employeeCorrection"
                      fill="#ff7300"
                      name="Employee Correction"
                    />
                    <Bar
                      dataKey="dependentCorrection"
                      fill="#ffc658"
                      name="Dependent Correction"
                    />
                    <Bar
                      dataKey="employeeDeletion"
                      fill="#ff0000"
                      name="Employee Deletion"
                    />
                    <Bar
                      dataKey="dependentDeletion"
                      fill="#d0ed57"
                      name="Dependent Deletion"
                    />
                  </BarChart>
                </ResponsiveContainer>

                <Table
                  title={() => (
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: "20px",
                        fontWeight: "bold",
                      }}
                    >
                      Month Wise Endorsement Data
                    </div>
                  )}
                  dataSource={endorsementData}
                  columns={[
                    { title: "Month", dataIndex: "month", key: "month" },
                    {
                      title: "EMPLOYEE ADDITION",
                      dataIndex: "employeeAddition",
                      key: "employeeAddition",
                    },
                    {
                      title: "DEPENDENT ADDITION",
                      dataIndex: "dependentAddition",
                      key: "dependentAddition",
                    },
                    {
                      title: "EMPLOYEE CORRECTION",
                      dataIndex: "employeeCorrection",
                      key: "employeeCorrection",
                    },
                    {
                      title: "DEPENDENT CORRECTION",
                      dataIndex: "dependentCorrection",
                      key: "dependentCorrection",
                    },
                    {
                      title: "EMPLOYEE DELETION",
                      dataIndex: "employeeDeletion",
                      key: "employeeDeletion",
                    },
                    {
                      title: "DEPENDENT DELETION",
                      dataIndex: "dependentDeletion",
                      key: "dependentDeletion",
                    },
                  ]}
                  rowKey="month"
                  style={{ marginTop: 20 }}
                  bordered
                />
              </Card>
            </Card>
          </>
        ) : null}
      </>
    ),
  }));

  return (
    <Spin spinning={loading}>
      <div className="escalation-matrix-container">
        <div className="escalation-content">
          <Row className="escalation-row-header">
            <span style={{ fontWeight: "700", fontSize: "20px" }}>
              {user?.corporate_name || "Guest"}
            </span>{" "}
            <span style={{ marginLeft: "8px" }}>
              {/* This now uses the 'selectedYear' variable derived from Redux state */}
              (Financial Year: {selectedYear})
            </span>
          </Row>
        </div>
        {policyTypes.length > 0 ? (
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            className="esc-tab"
            items={tabItems}
          />
        ) : (
          <Card style={{ marginTop: "20px" }}>
            <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
              <p style={{ fontSize: "16px", fontWeight: "500" }}>No endorsement analysis data available for {selectedYear}</p>
            </div>
          </Card>
        )}
      </div>
    </Spin>
  );
};

export default EndorsementAnalysis;